import { Link, useLocation } from "react-router-dom";
import Cookies from "js-cookie";
import {
    CSSObject,
    List,
    Theme,
    styled,
} from "@mui/material";
import MuiDrawer from "@mui/material/Drawer";
import sizeConfigs from "../../config/sizeConfigs";
import { useState } from "react";
import SidebarItemCollapse from "../../components/SidebarItemCollapse";
import SlidebarItemWithOutCollapse from "../../components/SlidebarItemWithOutCollapse";
import useAccessControl from "../../hooks/useAccessControl";
import { appAdminRoutes } from "../routes/adminRoutes";

type Props = {
    handleScreen: () => void;
};

const openedMixin = (theme: Theme): CSSObject => ({
    width: sizeConfigs.sidebar.width,
    transition: theme.transitions.create("width", {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
    }),
    overflowX: "hidden",
});

const closedMixin = (theme: Theme): CSSObject => ({
    transition: theme.transitions.create("width", {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    overflowX: "hidden",
    width: "0",
    [theme.breakpoints.up("sm")]: {
        width: "0",
    },
});


const Drawer = styled(MuiDrawer, {
    shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
    width: sizeConfigs.sidebar.width,
    flexShrink: 0,
    whiteSpace: "nowrap",
    boxSizing: "border-box",
    ...(open && {
        ...openedMixin(theme),
        "& .MuiDrawer-paper": openedMixin(theme),
    }),
    ...(!open && {
        ...closedMixin(theme),
        "& .MuiDrawer-paper": closedMixin(theme),
    }),
}));


export const SideBar = ({ handleScreen }: Props) => {
    const location = useLocation();
    const { permissions }: any = useAccessControl();
    const role = Cookies.get("role-velox-charter") || "";
    const isAdmin = role.toLowerCase() === "admin";
    const [open, setOpen] = useState<boolean>(true);

    return (
        <>
            <Drawer
                variant="permanent"
                open={open}
                sx={{
                    width: sizeConfigs.sidebar.width,
                    flexShrink: 0,
                    "& .MuiDrawer-paper": {
                        top: "100px",                  
                        height: "calc(100vh - 80px)",
                        backgroundColor: "#fff",
                        borderRight: "1px solid #D0D0D0",
                        overflow: "hidden",
                    },
                }}
            >
                <List
                    disablePadding
                    sx={{
                        maxHeight: "calc(100vh - 140px)",
                        overflowY: "auto",
                        paddingTop: "10px"
                    }}
                >
                    <div className="flex w-full px-[10px] mt-[10px] justify-end items-center border-b">
                        <span className={`material-symbols-outlined text-[30px] text-black hover:cursor-pointer`} onClick={() => {
                            setOpen(!open);
                            handleScreen();
                        }}>
                            arrow_left
                        </span>
                    </div>

                    <div className="w-full flex pt-[10px] flex-col items-start gap-1.5">
                        {appAdminRoutes.map((route: any, index) => {
                            if (isAdmin) {
                                return route.sidebarProps && route.child ? (
                                    <SidebarItemCollapse item={route} key={index} />
                                ) : (
                                    <SlidebarItemWithOutCollapse item={route} key={index} />
                                );
                            }
                            const hasAccessSection = route?.section ? permissions[route.section] : route.child ? route.child?.filter((child: any) => permissions[child.section] === true).length > 0 ? true : false : false;

                            const dashboardHasAccess = isAdmin || hasAccessSection;

                            if (dashboardHasAccess) {
                                return route.sidebarProps && route.child
                                    ? <SidebarItemCollapse item={route} key={index} />
                                    : <SlidebarItemWithOutCollapse item={route} key={index} />;
                            }

                            return null;
                        })}

                    </div>



                </List>

            </Drawer>

            {!open && (
                <div
                    className="sidebar close bg-white h-[calc(100vh-80px)] sticky top-[80px] flex flex-col border-r border-[#D0D0D0]"
                >
                    <div className={`flex items-center justify-center cursor-pointer mt-[10px] text-black hover:text-white hover:bg-primary border-b`}>
                        <span className={`material-symbols-outlined text-inherit text-[30px]`}
                            onClick={() => {
                                setOpen(!open);
                                handleScreen();
                            }}>
                            arrow_right
                        </span>
                    </div>

                    <ul className="nav-links max-h-[calc(100vh - 50px)]">
                        {appAdminRoutes?.map((item: any, index: number) => {
                            const hasCollapseAccessSection = item?.section ? isAdmin || permissions[item.section] : item.child ? item.child?.filter((child: any) => isAdmin || permissions[child.section] === true).length > 0 ? true : false : false;
                            const dashboardHasAccess = isAdmin || hasCollapseAccessSection;
                            let isActive = !item?.child ? location.pathname.startsWith(item?.path) ? true : false : false

                            if (dashboardHasAccess) {
                                return (
                                    item?.child
                                        ?
                                        <li key={index} className={`flex items-center justify-center p-[10px] cursor-pointer hover:bg-primary text-black hover:text-white`}>
                                            <div className="iocn-link">
                                                <span className={`material-symbols-outlined text-inherit text-[20px] `}>
                                                    {item?.sidebarProps?.icon}
                                                </span>

                                            </div>

                                            <ul className={`sub-menu bg-white rounded border border-solid border-[#D0D0D0]`}>
                                                <li className={`text-[#000000] font-semibold cursor-default`}>
                                                    <span className="link_name">
                                                        {item?.sidebarProps?.displayText}
                                                    </span>
                                                </li>

                                                {item?.child?.map((childItem: any, i: number) => {
                                                    const hasAccessChild = childItem?.section ? (isAdmin || permissions[childItem.section]) : false;
                                                    const isChildActive = location.pathname === childItem.path;
                                                    if (hasAccessChild) {

                                                        return (
                                                            <li key={i} className={`${isChildActive ? "bg-primary text-white" : "bg-white text-black"}  hover:bg-primary hover:text-white font-medium cursor-pointer`}>
                                                                <Link to={childItem?.path}>
                                                                    {childItem?.sidebarProps?.displayText}
                                                                </Link>
                                                            </li>
                                                        )
                                                    }

                                                    return null;
                                                })}
                                            </ul>
                                        </li>
                                        :
                                        <li key={index} className={`flex items-center justify-center p-[10px] cursor-pointer hover:bg-primary text-black hover:text-white`}>
                                            <span>
                                                <Link to={item?.path}>
                                                    <span className={`material-symbols-outlined text-inherit text-[20px]`}>
                                                        {item?.sidebarProps?.icon}
                                                    </span>
                                                </Link>
                                            </span>
                                            <ul className={`sub-menu blank bg-white rounded border border-solid border-[#D0D0D0]`}>
                                                <li className={`${isActive ? "bg-primary text-white" : "bg-white text-black"}  hover:bg-primary text-black hover:text-white font-medium cursor-pointer`}>
                                                    <Link to={item?.path} className="link_name">
                                                        {item?.sidebarProps?.displayText}
                                                    </Link>
                                                </li>
                                            </ul>
                                        </li>
                                )
                            }

                            return null;
                        })}


                    </ul>
                </div>

            )}
        </>
    );
};